import React, { useEffect, useState, useRef, useMemo } from 'react';
import swal from 'sweetalert2';

import { getStrokeExtraShot, getPlayerScore, getAdjustedHandicap } from '../tournamentHelper';
import { deleteScorecard, postScoreCard, sendScrambleFoursomesScorecardMail } from '../../api/tournamentAPI';

// Holes 0-8  => "front" nine (Holes 1-9)
// Holes 9-17 => "back" nine  (Holes 10-18)
// Which format (Scramble / Foursome) applies to which nine is controlled by
// `frontFormat` state below, and can be swapped with the toggle in the header,
// same pattern as BetterballGreensome. Default: front 9 = Scramble, back 9 = Foursome.
//
// Both formats here play ONE combined team score per hole (unlike Betterball,
// which needs two individual scores) - the only real differences are:
//   - Scramble holes show a "Drive" selector and enforce a min-drive rule
//   - Foursome holes have no drive tracking and no min-drive rule
// so most of the calculation logic below is shared between the two formats.
// NOTE: unlike BetterballGreensome, there is intentionally NO "min holes
// credited per player" quota logic here - each hole already has exactly one
// combined team score, so there's no "which player's score counts" decision
// to make in the first place.

const FRONT_HOLES = Array.from({ length: 9 }, (_, i) => i);      // 0..8
const BACK_HOLES = Array.from({ length: 9 }, (_, i) => i + 9);  // 9..17

// `format.RoundFormat` (when present) carries per-format handicap/min-drive config,
// falling back to the flat tournamentDraw.{Lower,Higher,MinDrive} fields if no
// per-format row is configured. Same helper as BetterballGreensome.
const getRoundFormatConfig = (roundFormatList, formatName, fallbackDraw) => {
    const match = (roundFormatList || []).find(
        (rf) => (rf.TournamentRoundFormat || '').toLowerCase() === formatName.toLowerCase()
    );
    if (match) return match;

    if (fallbackDraw) {
        return {
            TournamentRoundFormat: formatName,
            Lower: fallbackDraw.Lower,
            LowerMiddle: 0,
            Middle: 0,
            Higher: fallbackDraw.Higher,
            MinDrive: fallbackDraw.MinDrive,
            IsMin: !!fallbackDraw?.MinDrive ? 1 : 0,
        };
    }
    return null;
};

// Resolves the point table for one format from the flat pointData list, by
// PointName. Falls back to the FULL unfiltered list if nothing matches, so a
// PointName mismatch degrades gracefully (still scores something) instead of
// silently zeroing every hole for that format.
// NOTE: verify 'Scramble' / 'Foursome' against your actual API's PointName
// values - these are inferred from the naming used elsewhere in this file.
const buildFormatPointData = (pointsList, pointName) => {
    const filtered = (pointsList || []).filter((p) => p.PointName === pointName);
    const source = filtered.length > 0 ? filtered : (pointsList || []);
    return [...source].sort((a, b) => b.Score - a.Score);
};

const ScrambleFoursomes = ({
    playerList,
    tournamentDetails,
    tournamentDraw,
    tournamentRound,
    selectedPlayer,
    gameDetails,
    currentRound,
    format,
    scorecardStatus,
    setScorecardStatus,
    pointData,
    setShowPointModal,
    closeDrawer,
    fetchTournamentLeaderboard
}) => {

    // 'scramble' | 'foursome' — which format the FRONT nine (holes 1-9) plays.
    // The BACK nine (holes 10-18) always plays the other format.
    const [frontFormat, setFrontFormat] = useState('scramble');
    const backFormat = frontFormat === 'scramble' ? 'foursome' : 'scramble';

    const [scores,              setScores]              = useState(null);
    const [totalScores,         setTotalScores]         = useState([]);
    const [footerFrontScore,    setFooterFrontScore]    = useState('-');
    const [footerBackScore,     setFooterBackScore]     = useState('-');
    const [footerTotalScore,    setFooterTotalScore]    = useState('-');
    const [comment,             setComment]             = useState("");

    // Each format has its own combined (team) handicap, driven by that
    // format's own Higher/Lower percentages - Scramble holes always use the
    // Scramble row's percentages, Foursome holes always use the Foursome
    // row's, regardless of which nine (front/back) that format is currently
    // playing on.
    const [calculatedHandicapScramble, setCalculatedHandicapScramble] = useState();
    const [calculatedHandicapFoursome, setCalculatedHandicapFoursome] = useState();

    const [userTotal,      setUserTotal]      = useState(0); // gross total
    const [totalNetScore,  setTotalNetScore]  = useState(0); // net total

    const inputRefs = useRef([]);

    //#region Helpers - format per hole
    const getFormatForHole = (index) => (FRONT_HOLES.includes(index) ? frontFormat : backFormat);
    //#endregion

    // Per-format handicap / min-drive configuration, sourced from format.RoundFormat.
    // NOTE: 'Foursome' (singular) is used here to match the naming convention
    // used everywhere else in this file ('Scramble & Foursome', backFormat
    // values, UI labels) - verify this matches your backend's actual
    // TournamentRoundFormat value; it was previously mismatched as 'Foursomes'.
    const scrambleFormatData = useMemo(
        () => getRoundFormatConfig(format?.RoundFormat, 'Scramble', tournamentDraw),
        [format, tournamentDraw]
    );
    const foursomeFormatData = useMemo(
        () => getRoundFormatConfig(format?.RoundFormat, 'Foursomes', tournamentDraw),
        [format, tournamentDraw]
    );

    // Per-format point tables (falls back to the full list if PointName
    // doesn't match anything - see buildFormatPointData above).
    const scramblePointData = buildFormatPointData(pointData, 'Scramble');
    const foursomePointData = buildFormatPointData(pointData, 'Foursomes');

    //#region Effects
    useEffect(() => {
        const initialScores = {};

        if (!!tournamentDraw && !!selectedPlayer) {
            const scrambleHandicap = getAdjustedHandicap(
                scrambleFormatData?.Higher, scrambleFormatData?.Lower,
                selectedPlayer.Handicap, selectedPlayer.Player2Handicap
            );
            setCalculatedHandicapScramble(scrambleHandicap);

            const foursomeHandicap = getAdjustedHandicap(
                foursomeFormatData?.Higher, foursomeFormatData?.Lower,
                selectedPlayer.Handicap, selectedPlayer.Player2Handicap
            );
            setCalculatedHandicapFoursome(foursomeHandicap);
        }

        if (gameDetails.Card.length > 0) {
            gameDetails.Card.forEach((item) => {
                const index = item.HoleValue - 1;
                const holeFormat = getFormatForHole(index);

                initialScores[index] = {
                    player1Score: item.Scratch == "Y" ? "X" : item.Score,
                    driveUserID: holeFormat === 'scramble' ? (item.DriveUserID || selectedPlayer.PlayerID) : undefined
                };
            });

            for (let i = 0; i < 18; i++) {
                const holeFormat = getFormatForHole(i);
                if (!initialScores[i]) {
                    initialScores[i] = holeFormat === 'scramble'
                        ? { player1Score: 0, driveUserID: selectedPlayer.PlayerID }
                        : { player1Score: 0 };
                }
            }
        } else {
            gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
                const holeFormat = getFormatForHole(index);
                initialScores[index] = holeFormat === 'scramble'
                    ? { player1Score: 0, driveUserID: selectedPlayer.PlayerID }
                    : { player1Score: 0 };
            });
        }

        if (!!gameDetails?.Result) {
            let scStatus = (gameDetails?.Result.IsVerified == 'Y' ? 'verify' : (gameDetails?.Result.IsVerified == 'DQ' ? 'disqualify' : (gameDetails?.Result.IsVerified == 'WD' ? 'withdraw' : 'N')));
            setScorecardStatus(scStatus);
            setComment(gameDetails?.Result.Comment);
        }

        setScores(initialScores);
    }, [gameDetails, tournamentDraw, frontFormat, scrambleFormatData, foursomeFormatData]);

    useEffect(() => {
        if (scores) calculateScores(scores);
    }, [scores, calculatedHandicapScramble, calculatedHandicapFoursome, frontFormat]);

    useEffect(() => {
        if (selectedPlayer) {
            setScorecardStatus(selectedPlayer.IsVerified == 'Y' ? "verify" : selectedPlayer.IsVerified == "WD" ? 'withdraw' : selectedPlayer.IsVerified == 'DQ' ? 'disqualify' : '');
        }
    }, [selectedPlayer]);
    //#endregion

    //#region API's
    const deleteScoreCard = async () => {
        try {
            swal.fire({
                titleText: 'Loading', text: 'Please Wait...', showConfirmButton: false,
                onOpen: () => { swal.showLoading() }
            });

            await deleteScorecard(selectedPlayer.DrawID);
            fetchTournamentLeaderboard();
            swal.fire({
                icon: 'success', timer: 1500, animation: false, showConfirmButton: false,
                onOpen: () => swal.hideLoading(),
                titleText: 'Scorecard Updated Successfully!',
            });
            closeDrawer();
        } catch (err) {
            swal.fire({
                icon: 'error', buttonsStyling: false, confirmButtonClass: 'btn btn-brand',
                titleText: 'Error!', text: err.message,
                onOpen: () => swal.hideLoading()
            });
        }
    }

    const postScorecard = async (scoreCard) => {
        try {
            swal.fire({
                titleText: 'Loading', showConfirmButton: false,
                text: 'Please Wait...', onOpen: () => { swal.showLoading() }
            });

            await postScoreCard(scoreCard);
            fetchTournamentLeaderboard();
            swal.fire({
                icon: 'success', timer: 1500, animation: false, showConfirmButton: false,
                onOpen: () => swal.hideLoading(),
                titleText: 'Scorecard Updated Successfully!',
            });
            closeDrawer();
        } catch (err) {
            swal.fire({
                icon: 'error', buttonsStyling: false, confirmButtonClass: 'btn btn-brand',
                titleText: 'Error!', text: err.message,
                onOpen: () => swal.hideLoading()
            });
        }
    }
    //#endregion

    //#region Helpers
    const handleDriveUserChange = (index, value) => {
        setScores(s => ({ ...s, [index]: { ...s[index], driveUserID: value } }));
    }

    const calculateScores = (updatedScores) => {
        const createPointFor = (sortedPoints) => {
            const maxScore = sortedPoints[0]?.Score || 0;
            const minScore = sortedPoints[sortedPoints.length - 1]?.Score || 0;

            return (adjScore) => {
                let clamped = adjScore;
                if (clamped > maxScore) clamped = maxScore;
                if (clamped < minScore) clamped = minScore;
                const match = $.grep(sortedPoints, (s) => s.Score === clamped)[0];
                return match ? match.Point : 0;
            };
        };

        const scramblePointFor = createPointFor(scramblePointData);
        const foursomePointFor = createPointFor(foursomePointData);

        const updatedTotalScores = [];
        let cumulativeFront = 0, cumulativeBack = 0;
        let allFrontZero = true, allBackZero = true;
        let cumulativeUserTotal = 0, cumulativeNetTotal = 0;

        gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
            const holeFormat = getFormatForHole(index);
            const hole = updatedScores[index] || {};

            // Each format uses its OWN handicap and point table - Scramble
            // holes never borrow Foursome's numbers or vice versa, regardless
            // of which nine either format is currently playing on.
            const handicapForHole = holeFormat === 'scramble' ? calculatedHandicapScramble : calculatedHandicapFoursome;
            const pointFor = holeFormat === 'scramble' ? scramblePointFor : foursomePointFor;

            const player1Input = hole.player1Score || 0;
            const p1 = getPlayerScore(player1Input, handicapForHole, item.Stroke, item.Par);

            cumulativeUserTotal += p1.grossScore;
            cumulativeNetTotal += p1.netScore;

            let player1Point = 0;
            if (p1.grossScore != 0) {
                const adj = (!!p1.IsScratch) ? p1.netScore : (p1.netScore - item.Par);
                player1Point = pointFor(adj);
            }

            const totalScore = player1Point;

            if (totalScore != 0) {
                if (FRONT_HOLES.includes(index)) { allFrontZero = false; cumulativeFront += totalScore; }
                else { allBackZero = false; cumulativeBack += totalScore; }
            }

            updatedTotalScores[index] = {
                HoleNo: item.HoleNoValue,
                format: holeFormat,
                player1GrossScore: p1.grossScore,
                player1NetScore: p1.netScore,
                player1Point,
                maxTotal: totalScore,
                player1Scratch: p1.IsScratch,
                driveUserID: holeFormat === 'scramble' ? (hole.driveUserID || selectedPlayer.PlayerID) : undefined,
            };
        });

        setTotalScores(updatedTotalScores);
        setUserTotal(cumulativeUserTotal);
        setTotalNetScore(cumulativeNetTotal);
        setFooterFrontScore(allFrontZero ? '-' : cumulativeFront);
        setFooterBackScore(allBackZero ? '-' : cumulativeBack);
        setFooterTotalScore((allFrontZero && allBackZero) ? '-' : (cumulativeFront + cumulativeBack));
    };

    const handleScoreChange = (index, value) => {
        if (!(/^[0-9X]*$/).test(value)) return;

        const oldScore = scores[index];
        const updatedScores = { ...scores, [index]: { ...oldScore, player1Score: value } };
        setScores(updatedScores);

        if (inputRefs.current[index + 1]) {
            inputRefs.current[index + 1].focus();
            inputRefs.current[index + 1].select();
        }
    };

    const toggleStatus = (value) => setScorecardStatus(prevStatus => (prevStatus == value ? '' : value));

    const swapFormats = () => {
        setFrontFormat(prev => (prev === 'scramble' ? 'foursome' : 'scramble'));
        setScores(null); // force re-init on the new hole/format mapping
    };

    const saveScorecard = () => {
        // Min-drive requirement always comes from the Scramble row of RoundFormat
        // (falls back to tournamentDraw.MinDrive if that row isn't configured) -
        // Foursome has no drive tracking, so it never contributes here.
        const minDrive = scrambleFormatData?.MinDrive ?? tournamentDraw.MinDrive;
        const isMinDriveEnforced = scrambleFormatData ? !!scrambleFormatData.IsMin : true;

        const drivePlayer1 = [];
        const drivePlayer2 = [];
        let driveUser = '';
        let notGoodMinDrive = false;

        let newScoreCard = [];
        let notComplete = false;
        let hole = 0;

        const player1 = { id: selectedPlayer.PlayerID, name: selectedPlayer.Player1 };
        const player2 = { id: selectedPlayer.Player2ID, name: selectedPlayer.Player2 };

        gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
            const holeFormat = getFormatForHole(index);
            const cHole = item.HoleNoValue;
            let cScore = totalScores.find(t => t.HoleNo == cHole);

            if ((isNaN(cScore.player1GrossScore) || cScore.player1GrossScore == 0) && scorecardStatus == 'verify') {
                notComplete = true;
                return;
            }

            let driveId = 0;
            if (holeFormat === 'scramble') {
                driveId = cScore.driveUserID;
                const driveName = driveId == player1.id ? player1.name : player2.name;
                const drive = { id: driveId, name: driveName };
                if (drive.id == player1.id) drivePlayer1.push(drive);
                else drivePlayer2.push(drive);
            }

            newScoreCard.push({
                ID                  : 0,
                TournamentDrawID    : selectedPlayer.DrawID,
                PlayerID            : selectedPlayer.PlayerID,
                PlayerType          : selectedPlayer.Player1Type,
                ScoreCardMasterID   : item.ID,
                Score               : cScore.player1GrossScore,
                HandicapScore       : cScore.player1NetScore,
                DriveUserID         : driveId,
                Point               : cScore.player1Point,
                TotalPoint          : cScore.player1Point,
                Scratch             : (cScore.player1Scratch) ? "Y" : "N",
            });

            if (cScore.player1GrossScore != 0) hole++;
        });

        if (notComplete) {
            swal.fire({ icon: "warning", titleText: "Incomplete Scorecard", text: "Please complete the scorecard..!" });
            return;
        }

        // Min-drive check only applies across the scramble holes actually
        // played, and only when the Scramble format row has IsMin enabled.
        const scrambleHolesCount = drivePlayer1.length + drivePlayer2.length;
        if (scrambleHolesCount > 0 && isMinDriveEnforced && minDrive > 0) {
            if (drivePlayer1.length < minDrive) driveUser = `"${player1.name}"`;
            if (drivePlayer2.length < minDrive) driveUser += `"${player2.name}"`;
            if (drivePlayer1.length < minDrive || drivePlayer2.length < minDrive) notGoodMinDrive = true;
        }

        if (notGoodMinDrive) {
            swal.fire({ icon: "warning", titleText: "Invalid Drive", text: `The player ${driveUser} should get at least ${minDrive} min drive` });
            return;
        }

        const result = {
            ID: 0, Hole: hole, TournamentDrawID: selectedPlayer.DrawID,
            Point      : footerTotalScore == '-' ? 0 : footerTotalScore,
            TotalPoint : footerTotalScore == '-' ? 0 : footerTotalScore,
            IsVerified : (scorecardStatus == 'verify') ? 'Y' : (scorecardStatus == 'disqualify' ? 'DQ' : (scorecardStatus == 'withdraw' ? 'WD' : 'N')),
            Comment    : (scorecardStatus == 'disqualify' || scorecardStatus == 'withdraw') ? comment : '',
            FrontFormat: frontFormat, BackFormat: backFormat
        };

        const dto = { scoreCard: newScoreCard, result: result };
        postScorecard(dto);
    }

    const sendScorecard = async () => {
        try {
            swal.fire({ showConfirmButton: false, onOpen: () => swal.showLoading(), titleText: 'Loading', text: 'Please Wait...', allowOutsideClick: false, allowEscapeKey: false });

            let playerNameList = [];
            // Round mixes two formats with two different handicaps - show both
            // rather than picking one arbitrarily.
            playerNameList.push({
                ID: 1,
                Name: `${playerList["Player1"].UserName} | ${playerList["Player2"].UserName} - Scramble ${calculatedHandicapScramble} / Foursomes ${calculatedHandicapFoursome}`,
                Email: playerList["Player1"].Email + ';' + playerList["Player2"].Email
            });

            let scoreCardList = [];
            gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
                const cHole = item.HoleNoValue;
                let cScore = totalScores.find(t => t.HoleNo == cHole);
                const driveName = (cScore.format === 'scramble')
                    ? (cScore.driveUserID == selectedPlayer.PlayerID ? selectedPlayer.Player1 : selectedPlayer.Player2)
                    : '-';

                scoreCardList.push({
                    Hole                : cHole,
                    Par                 : item.Par,
                    Stroke              : item.Stroke,
                    Format              : cScore.format,
                    Drive               : driveName,
                    Player1GrossScore   : cScore.player1GrossScore,
                    Player1NetScore     : cScore.player1NetScore,
                    Player1Point        : cScore.player1Point,
                    Total               : cScore.maxTotal
                });
            });

            const tournament = {
                ID      : tournamentDetails.ID,
                Name    : tournamentDetails.Name,
                Format  : format?.Format,
                Round   : selectedPlayer.Round,
                Date    : tournamentDetails.StartDate
            };

            await sendScrambleFoursomesScorecardMail(
                playerNameList,
                selectedPlayer.Player1, selectedPlayer.Player2,
                scoreCardList, gameDetails.TeeBoxes.Par,
                {calculatedHandicapScramble, calculatedHandicapFoursome},
                footerFrontScore, footerBackScore, footerTotalScore == '-' ? 0 : footerTotalScore,
                tournament
            );

            swal.close();
            swal.fire({
                icon: 'success',
                titleText: 'Scorecard Email Sent',
                showConfirmButton: false,
                timer: 1500,
            });
        } catch (err) {
            swal.fire({ icon: 'error', titleText: err.message, onOpen: () => swal.hideLoading() });
        }
    }
    //#endregion

    //#region Render helpers
    const renderTableHead = (holeFormat) => (
        <>
            <tr>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Hole</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Par</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Stroke</th>
                {holeFormat === 'scramble' ? (
                    <>
                        <th rowSpan={2} style={{ textAlign: 'center', width: '15%', verticalAlign: 'middle' }}>Drive</th>
                        <th colSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            {selectedPlayer.Player1Nick || selectedPlayer.Player1} | {selectedPlayer.Player2Nick || selectedPlayer.Player2} ({calculatedHandicapScramble}){(scrambleFormatData?.Lower != 100) || (scrambleFormatData?.Higher != 100) ? '*' : ''}
                        </th>
                    </>
                ) : (
                    <th colSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {selectedPlayer.Player1Nick || selectedPlayer.Player1} | {selectedPlayer.Player2Nick || selectedPlayer.Player2} ({calculatedHandicapFoursome}){(foursomeFormatData?.Lower != 100) || (foursomeFormatData?.Higher != 100) ? '*' : ''}
                    </th>
                )}
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Total Score</th>
            </tr>
            <tr>
                <th className="text-center">Gross Score</th>
                <th className="text-center">Net Score</th>
                <th className="text-center">Points</th>
            </tr>
        </>
    );

    const renderRow = (index, item) => {
        if (!scores || !scores[index]) return null;
        const holeFormat = getFormatForHole(index);
        const totalScore = totalScores[index] || {};
        const player1Score = scores[index]?.player1Score || 0;
        const handicapForHole = holeFormat === 'scramble' ? calculatedHandicapScramble : calculatedHandicapFoursome;
        const player1ExtraShot = (getStrokeExtraShot(item.Stroke, handicapForHole) || 0);
        const driveUserID = scores[index]?.driveUserID || selectedPlayer.PlayerID;

        return (
            <tr key={`row_${item.HoleNoValue}`} className="row-score" data-masterid={item.ID}>
                <td className='text-center'>{item.HoleNoValue}</td>
                <td className='text-center'>{item.Par}</td>
                <td className='text-center'>{item.Stroke}</td>
                {holeFormat === 'scramble' && (
                    <td>
                        <select className="form-drive form-control" tabIndex="-1"
                            onChange={(e) => handleDriveUserChange(index, e.target.value)}
                            value={driveUserID}>
                            <option value={selectedPlayer.PlayerID}>{selectedPlayer.Player1}</option>
                            <option value={selectedPlayer.Player2ID}>{selectedPlayer.Player2}</option>
                        </select>
                    </td>
                )}
                <td className="player-td" data-extra={player1ExtraShot}>
                    <div className="input-group">
                        <input
                            className="form-control input-sm input-numeric" type="text" value={player1Score}
                            ref={el => inputRefs.current[index] = el}
                            onChange={(e) => handleScoreChange(index, parseInt(e.target.value))}
                        />
                        <div className="input-group-append">
                            <span className="input-group-text clickable" onClick={() => handleScoreChange(index, "X")}><i className="la la-times"></i></span>
                        </div>
                    </div>
                </td>
                <td className="card-score text-center">{totalScore.player1NetScore}</td>
                <td className="card-point text-center">{totalScore.player1Point}</td>
                <td className="card-total-score text-center">{totalScore.maxTotal}</td>
            </tr>
        );
    };

    const renderNineTable = (holeIndices, label, footerScore) => {
        if (!gameDetails || !scores) return null;
        const holeFormat = getFormatForHole(holeIndices[0]);
        const colCount = holeFormat === 'scramble' ? 8 : 7; // Hole,Par,Stroke,[Drive],Gross,Net,Points,Total

        return (
            <div className="table-register mb-4 mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 font-weight-bold">{label} <span className="text-muted">({holeFormat === 'scramble' ? 'Scramble' : 'Foursomes'})</span></h6>
                </div>
                <table id="table-scorecard" className="table table-bordered table-striped table-card">
                    <thead>{renderTableHead(holeFormat)}</thead>
                    <tbody>
                        {holeIndices.map((idx) => renderRow(idx, gameDetails.TeeBoxes.ScoreCard[idx]))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-light font-weight500">
                            <td className='text-center'>{label.startsWith('OUT') ? 'OUT' : 'IN'}</td>
                            <td className='text-center'>
                                {holeIndices.reduce((sum, i) => sum + (gameDetails.TeeBoxes.ScoreCard[i]?.Par || 0), 0)}
                            </td>
                            {Array.from({ length: colCount - 3 }).map((_, i) => <td key={i} className='text-center'></td>)}
                            <td className='text-center font-weight-bold'>{footerScore}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };
    //#endregion

    if (!gameDetails || !scores) return null;

    return (<>
        <div className="kt-portlet kt-portlet--mobile padding-lr20">
            <div className="kt-portlet__head kt-portlet__head--lg d-block padding-lr0 border-0">

                <div id="roundInfoPanel" className="kt-notification margin-t10 mb-3" style={{ backgroundColor: "rgb(247, 248, 250)" }}>
                    <div className="kt-notification__item d-block">
                        <div className="row">
                            <div className="col-3">
                                <div className="kt-user-card-v2 mb-2">
                                    <div className="kt-user-card-v2__pic">
                                        <img id="txtTournamentImg" src={tournamentDetails?.Icon} alt="tournament-logo" />
                                    </div>
                                    <div className="kt-user-card-v2__details ">
                                        <span id="txtTournament" className="kt-user-card-v2__name text-clip"> {tournamentDetails?.Name} </span>
                                        <span className="kt-user-card-v2__desc ">
                                            <span id="txtRound"> Round {selectedPlayer?.Round} </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2 d-flex-center">
                                <div className="kt-user-card-v2 ">
                                    <div className="kt-user-card-v2__details ">
                                        <span className="kt-user-card-v2__name ">Date </span>
                                        <span className="kt-user-card-v2__desc">
                                            <span id="txtStartDate">{moment(tournamentDetails?.StartDate).format('DD-MMM-YYYY')}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2 d-flex-center">
                                <div className="kt-user-card-v2">
                                    <div className="kt-user-card-v2__details ">
                                        <span className="kt-user-card-v2__name ">Format </span>
                                        <span className="kt-user-card-v2__desc ">
                                            <span id="txtFormat">{format?.Format || 'Scramble & Foursomes'}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {!!tournamentRound && tournamentRound?.length > 0 && (
                                <div className="col-2 d-flex-center">
                                    <div className="kt-user-card-v2 mb-2">
                                        <div className="kt-user-card-v2__details ">
                                            <span className="kt-user-card-v2__name ">Tee Of Option </span>
                                            <span className="kt-user-card-v2__desc ">
                                                <span id="txtTeeOption">{format?.TeeOffOption}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="col-1 d-flex-center">
                                <div className="kt-user-card-v2 ">
                                    <div className="kt-user-card-v2__details ">
                                        <span className="kt-user-card-v2__name " id="lbltxtSlot"> Slot </span>
                                        <span className="kt-user-card-v2__desc ">
                                            <span id="txtSlot">{selectedPlayer.Slot}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2 d-flex-center justify-content-end">
                                {!!pointData && pointData.length > 0 && (
                                    <button id="btnPoint" type="button" className="btn btn-secondary btn-elevate btn-pill btn-sm m-0 mr-2"
                                        onClick={() => setShowPointModal(true)}>
                                        <i className="la la-list"></i> Point
                                    </button>
                                )}
                                {/* {gameDetails.Card.length === 0 && (
                                    <button id="btnSwapFormat" type="button" className="btn btn-outline-brand btn-elevate btn-pill btn-sm m-0"
                                        onClick={swapFormats} title="Swap which nine plays Scramble vs Foursome">
                                        <i className="la la-exchange"></i> Swap 9s
                                    </button>
                                )} */}
                            </div>
                        </div>

                        <hr />
                        <div className="row">
                            <div className="col-12">
                                <small className="text-muted">
                                    Front 9 (Holes 1-9): <strong>{frontFormat === 'scramble' ? 'Scramble' : 'Foursomes'}</strong>
                                    &nbsp;|&nbsp;
                                    Back 9 (Holes 10-18): <strong>{backFormat === 'scramble' ? 'Scramble' : 'Foursomes'}</strong>
                                </small>
                            </div>
                        </div>
                        <hr />

                        <div className="row">
                            <div className="col-9">
                                <label className="kt-checkbox kt-checkbox--bold kt-checkbox--success mb-0">
                                    <input
                                        value="verify"
                                        name='verify'
                                        checked={scorecardStatus == 'verify'}
                                        onChange={() => toggleStatus('verify')}
                                        type="checkbox"
                                        className="form-verify" />
                                    Verify Scorecard <span></span>
                                </label>
                            </div>
                            <div className="col-3 text-right">
                                <div className="kt-checkbox-inline">
                                    <label className="kt-checkbox kt-checkbox--bold kt-checkbox--danger mb-0 mr-2">
                                        <input
                                            value="disqualify"
                                            name='disqualify'
                                            checked={scorecardStatus == 'disqualify'}
                                            onChange={() => toggleStatus('disqualify')}
                                            type="checkbox"
                                            className="form-disqualify" />
                                        Disqualify <span></span>
                                    </label>
                                    <label className="kt-checkbox kt-checkbox--bold kt-checkbox--brand mb-0">
                                        <input
                                            value="withdraw"
                                            checked={scorecardStatus == 'withdraw'}
                                            onChange={() => toggleStatus('withdraw')}
                                            name='withdraw'
                                            type="checkbox"
                                            className="form-withdraw" />
                                        Withdraw <span></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        {(scorecardStatus == 'withdraw' || scorecardStatus == 'disqualify') && (
                            <div className="row justify-content-end mt-2" id="pnlComment">
                                <div className="col-4 text-right">
                                    <textarea
                                        name="txtComment"
                                        id="txtComment"
                                        className="form-control"
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        cols="3" rows="2" placeholder="Comments"
                                    ></textarea>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {renderNineTable(FRONT_HOLES, 'OUT (1-9)', footerFrontScore)}
                {renderNineTable(BACK_HOLES, 'IN (10-18)', footerBackScore)}

                <div className="row">
                    <div className="col-12 text-right">
                        <h5>Total: <span id="lblGrandTotal">{footerTotalScore}</span></h5>
                    </div>
                </div>
            </div>

            <div className="kt-portlet__foot">
                <div className="kt-form__actions">
                    <div className="row">
                        <div className="col-lg-12 ml-lg-auto text-center">
                            {(selectedPlayer.IsVerified == 'Y' || selectedPlayer.IsVerified == 'WD' || selectedPlayer.IsVerified == 'DQ') && (
                                <button id="btnEmailCard" type="button" className="btn btn-info mr-2" onClick={sendScorecard}><i className="fa fa fa-envelope bigger-110"></i> Email Card </button>
                            )}
                            <button id="btnDelete" type="button" className="btn btn-danger d-inline-block mr-4" onClick={deleteScoreCard}> Delete </button>
                            <button id="btnSave" type="button" className="btn btn-success d-inline-block mr-2" onClick={saveScorecard}> Save </button>
                            <button id="btnBack" type="button" className="btn btn-secondary" onClick={closeDrawer}> Close </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>);
}

export default ScrambleFoursomes;